import { makeObservable, observable, action, computed, runInAction, toJS } from 'mobx';
import { getShopSettings } from '../helpers/api';

// Import specialized stores
import servicesStore from './ServicesStore';
import shopSettingsStore from './ShopSettingsStore';
import bookingsStore from './BookingsStore';
import usersManagementStore from './UsersManagementStore';
import employeesManagementStore from './EmployeesManagementStore';

/**
 * AdminStore - Shared data and common functionality for all admin stores
 * Contains:
 * - Shop settings (used across entire app)
 * - Common error/success/loading state management (to avoid duplication)
 */
class AdminStore {
  // Truly shared data across the app
  _shopSettings = null;
  _shopSettingsLoaded = false;

  constructor() {
    makeObservable(this, {
      _shopSettings: observable,
      _shopSettingsLoaded: observable,

      shopSettings: computed,
      shopName: computed,
      theme: computed,

      loadShopSettings: action
    });
  }

  // Getters
  get shopSettings() {
    return toJS(this._shopSettings);
  }

  get shopName() {
    return this._shopSettings?.name || 'barba';
  }

  get theme() {
    return this._shopSettings?.theme || 'sunset';
  }

  // Load shop settings (cached - only loads once)
  async loadShopSettings() {
    if (this._shopSettingsLoaded && this._shopSettings) {
      return this._shopSettings;
    }

    try {
      const data = await getShopSettings();
      runInAction(() => {
        this._shopSettings = data.settings || null;
        this._shopSettingsLoaded = true;
      });
      return this._shopSettings;
    } catch (err) {
      console.error('Failed to load shop settings:', err);
      throw err;
    }
  }

  // Common helper for async operations with error handling
  // Can be used by other stores to avoid duplication
  static async executeAsync(store, operation, successMessage = null) {
    store._isLoading = true;
    store._error = null;

    try {
      const result = await operation();
      runInAction(() => {
        if (successMessage) {
          store._successMessage = successMessage;
        }
        store._isLoading = false;
      });
      return result;
    } catch (err) {
      runInAction(() => {
        store._error = err.message || 'An error occurred';
        store._isLoading = false;
      });
      throw err;
    }
  }
}

// Create singleton instance
const adminStore = new AdminStore();

// Export all stores
export {
  servicesStore,
  shopSettingsStore,
  bookingsStore,
  usersManagementStore,
  employeesManagementStore
};

export default adminStore;
